import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import engageService, {
    DataGolTokenResponse,
    LoadStatus,
    ENGAGE_UNAUTHORIZED_STATE,
    ENGAGE_FAILED_STATE,
    UNAUTHORIZED_REDIRECT_DELAY_MS,
    getLoadStatusFromError,
} from '../../features/engage/engageService';
import { RootState } from '../../app/store';
import config from '../../app/config';
import { NoResultsFound } from '../../components/common/NoResultsFound';
import { forceLogoutToLogin } from '../../utils/auth/forceLogout';
import { useScript } from '../../hooks/useScript';
import './EngageDashboard.scss';

declare global {
    interface Window {
        DataGOL?: {
            init: (config: {
                elementId: string;
                appId: string;
                dashboardId: string;
                authToken: string;
                onSessionExpire?: () => Promise<string>;
            }) => void;
        };
    }
}

const DASHBOARD_ELEMENT_ID = 'engage-dashboard';

export const EngageDashboard = () => {
    const location = useLocation();
    const [token, setToken] = useState<DataGolTokenResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('ok');
    const scriptStatus = useScript(config.dataGolEmbedScriptUrl);
    const tenantCode = useSelector<RootState, string>(
        (state) => (state?.users?.user as any)?.defaultTenant || ''
    );
    const userLoading = useSelector<RootState, boolean>(
        (state) => state?.users?.loading ?? false
    );

    const { appId, dashboardId } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            appId: params.get('appId') || '',
            dashboardId: params.get('dashboardId') || '',
        };
    }, [location.search]);

    useEffect(() => {
        if (!appId || !dashboardId) {
            setLoadStatus('failed');
            setIsLoading(false);
            return;
        }

        // Wait for the Redux user to settle before deciding whether the tenant
        // is missing — otherwise we'd flash a "failed" state during the initial
        // retrieveUser fetch.
        if (userLoading) {
            return;
        }

        if (!tenantCode) {
            setLoadStatus('failed');
            setIsLoading(false);
            return;
        }

        engageService.getDataGolToken({ tenantCode, dashboardId })
            .then((response) => {
                setToken(response.data);
                setLoadStatus('ok');
            })
            .catch((error) => {
                setLoadStatus(getLoadStatusFromError(error));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [appId, dashboardId, tenantCode, userLoading]);

    const handleSessionExpire = useCallback(async (): Promise<string> => {
        const response = await engageService.getDataGolToken({ tenantCode, dashboardId });
        return response.data.userToken;
    }, [tenantCode, dashboardId]);

    useEffect(() => {
        if (scriptStatus === 'error') {
            setLoadStatus('failed');
            return;
        }

        if (!token || scriptStatus !== 'ready') return;

        if (!window.DataGOL || typeof window.DataGOL.init !== 'function') {
            setLoadStatus('failed');
            return;
        }

        window.DataGOL.init({
            elementId: DASHBOARD_ELEMENT_ID,
            appId,
            dashboardId,
            authToken: token.userToken,
            onSessionExpire: handleSessionExpire,
        });
    }, [token, appId, dashboardId, handleSessionExpire, scriptStatus]);

    useEffect(() => {
        if (loadStatus !== 'unauthorized') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            forceLogoutToLogin();
        }, UNAUTHORIZED_REDIRECT_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [loadStatus]);

    const showError = !isLoading && (loadStatus !== 'ok' || !token);
    const errorState = loadStatus === 'unauthorized' ? ENGAGE_UNAUTHORIZED_STATE : ENGAGE_FAILED_STATE;

    return (
        <div id={DASHBOARD_ELEMENT_ID} className="engage-dashboard-container">
            {showError && (
                <NoResultsFound
                    message={errorState.message}
                    description={errorState.description}
                />
            )}
        </div>
    );
};
